import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer, morphSection } from '@theme/section-renderer';

class RecentlyViewedProducts extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('pageshow', this.#onPageShow);
    if (!this.dataset.loaded) this.#loadProducts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#loadProducts();
  };

  async #loadProducts() {
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    const { sectionId, productId, limit, url: searchUrl } = this.dataset;
    const productLimit = Number(limit);
    if (!sectionId || !searchUrl || !Number.isInteger(productLimit) || productLimit < 1) return;

    const productIds = RecentlyViewed.getProducts(productLimit + 1)
      .filter((id) => id !== productId)
      .slice(0, productLimit);

    if (!productIds.length) {
      this.hidden = true;
      return;
    }

    try {
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[unavailable_products]', 'show');
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url, controller.signal);
      if (controller.signal.aborted || !this.isConnected) return;

      const document = new DOMParser().parseFromString(html, 'text/html');
      const section = document.getElementById(this.id);
      if (!section?.querySelector('[data-has-recommendations="true"]')) {
        this.hidden = true;
        return;
      }

      // Search rendering has no current product; preserve the PDP context for later refreshes.
      section.dataset.productId = productId || '';
      section.dataset.loaded = 'true';
      section.hidden = false;
      await morphSection(sectionId, document.documentElement.outerHTML, {
        mode: 'hydration',
        injectStylesheet: true,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      this.hidden = true;
      console.error('Recently viewed products could not be loaded:', error);
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
